import { useCallback, useEffect, useState } from 'react';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import API from '../api/axios';

const EMPTY_BANNER = {
  kicker: '', title: '', body: '', image_url: '', font_family: 'Georgia, serif',
  text_color: '#FFFFFF', accent_color: '#D6B24A', overlay_color: '#111111',
  color_theme: 'gold-night', font_size: 56, text_alignment: 'center',
  image_position_x: 50, image_position_y: 50, text_position_x: 50, text_position_y: 50,
  sort_order: 0, is_active: true,
};
const FONTS = ['Georgia, serif', 'Arial, sans-serif', 'Trebuchet MS, sans-serif', 'Courier New, monospace'];
const COLOR_THEMES = {
  'gold-night': { text_color: '#FFFFFF', accent_color: '#D6B24A', overlay_color: '#111111' },
  ivory: { text_color: '#25221D', accent_color: '#8D6721', overlay_color: '#F5EBDD' },
  'coral-sunrise': { text_color: '#FFF9F3', accent_color: '#FFD166', overlay_color: '#702E35' },
  forest: { text_color: '#F7F5E9', accent_color: '#C7D98B', overlay_color: '#183C32' },
};

export default function BannerManager() {
  const [banners, setBanners] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_BANNER);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showArchived, setShowArchived] = useState(false);

  const loadBanners = useCallback(async () => {
    try {
      const response = await API.get('/banners/admin/all', { params: { include_archived: showArchived } });
      setBanners(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not load banners');
    }
  }, [showArchived]);

  useEffect(() => { loadBanners(); }, [loadBanners]);

  const updateForm = (key, value) => setForm(current => ({ ...current, [key]: value }));
  const updateColor = (key, value) => setForm(current => ({ ...current, [key]: value, color_theme: 'custom' }));
  const applyTheme = theme => setForm(current => ({
    ...current,
    color_theme: theme,
    ...(COLOR_THEMES[theme] || {}),
  }));

  const handleImageUpload = async event => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Choose an image file');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Banner images must be 10 MB or smaller');
      return;
    }

    setUploading(true);
    try {
      const { data: signature } = await API.post('/banners/admin/upload-signature');
      const payload = new FormData();
      payload.append('file', file);
      payload.append('api_key', signature.api_key);
      payload.append('timestamp', signature.timestamp);
      payload.append('folder', signature.folder);
      payload.append('upload_preset', signature.upload_preset);
      payload.append('signature', signature.signature);
      const response = await fetch(`https://api.cloudinary.com/v1_1/${signature.cloud_name}/image/upload`, {
        method: 'POST', body: payload,
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error?.message || 'Image upload failed');
      updateForm('image_url', result.secure_url);
      toast.success('Banner image uploaded');
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || 'Image upload failed');
    } finally {
      setUploading(false);
    }
  };

  const startEdit = banner => {
    setEditingId(banner.id);
    setForm({ ...EMPTY_BANNER, ...banner });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const resetForm = () => {
    setEditingId(null);
    setForm(EMPTY_BANNER);
  };

  const saveBanner = async event => {
    event.preventDefault();
    if (!form.title.trim() || !form.image_url.trim()) {
      toast.error('A title and uploaded image are required');
      return;
    }
    setSaving(true);
    try {
      if (editingId) await API.put(`/banners/admin/${editingId}`, form);
      else await API.post('/banners/admin', form);
      toast.success(editingId ? 'Banner updated' : 'Banner added');
      resetForm();
      await loadBanners();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not save banner');
    } finally {
      setSaving(false);
    }
  };

  const archiveBanner = async banner => {
    if (!window.confirm(`Archive banner “${banner.title}”?`)) return;
    if (window.prompt('Type ARCHIVE to confirm:') !== 'ARCHIVE') return;
    try {
      await API.post(`/banners/admin/${banner.id}/archive`);
      toast.success('Banner archived');
      await loadBanners();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not archive banner');
    }
  };

  const restoreBanner = async banner => {
    try {
      await API.post(`/banners/admin/${banner.id}/restore`);
      toast.success('Banner restored');
      await loadBanners();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not restore banner');
    }
  };

  const toggleActive = async banner => {
    try {
      await API.put(`/banners/admin/${banner.id}`, { ...banner, is_active: !banner.is_active });
      await loadBanners();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not update banner');
    }
  };

  const fieldStyle = {
    width: '100%', padding: '0.65rem 0.8rem', border: '1px solid var(--light-gray)',
    borderRadius: '6px', background: 'var(--white)', font: 'inherit',
  };
  const rangeStyle = { width: '100%', accentColor: 'var(--warm-brown)', cursor: 'pointer' };
  const previewBackground = form.image_url
    ? `linear-gradient(${form.overlay_color}B8, ${form.overlay_color}55), url("${form.image_url}")`
    : `linear-gradient(135deg, ${form.overlay_color}, #55452F)`;

  return (
    <section>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
        <div>
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.25rem' }}>Hero slideshow</h2>
          <p style={{ color: 'var(--mid-gray)', fontSize: '0.88rem', marginTop: '0.25rem' }}>Add seasonal banners and control their order.</p>
        </div>
        <button type="button" onClick={() => setShowArchived(value => !value)} style={{ ...fieldStyle, width: 'auto', cursor: 'pointer' }}>
          {showArchived ? 'Show active banners' : 'Archived banners'}
        </button>
      </div>

      {!showArchived && (
        <form onSubmit={saveBanner} style={{ padding: '1rem', border: '1px solid var(--light-gray)', borderRadius: '8px', marginBottom: '1.25rem' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: '0.8rem' }}>
            <label>Kicker<input value={form.kicker} onChange={event => updateForm('kicker', event.target.value)} maxLength={120} style={fieldStyle} /></label>
            <label>Headline *<input value={form.title} onChange={event => updateForm('title', event.target.value)} maxLength={180} required style={fieldStyle} /></label>
            <label>Supporting text<textarea value={form.body} onChange={event => updateForm('body', event.target.value)} rows={2} style={fieldStyle} /></label>
            <label>Image URL<input value={form.image_url} onChange={event => updateForm('image_url', event.target.value)} type="url" required style={fieldStyle} /></label>
            <label>Upload image (max 10 MB)<input type="file" accept="image/*" onChange={handleImageUpload} disabled={uploading} style={{ ...fieldStyle, padding: '0.45rem' }} /></label>
            <label>Font<select value={form.font_family} onChange={event => updateForm('font_family', event.target.value)} style={fieldStyle}>{FONTS.map(font => <option key={font} value={font}>{font.split(',')[0]}</option>)}</select></label>
            <label>Color theme<select value={form.color_theme} onChange={event => applyTheme(event.target.value)} style={fieldStyle}>
              {Object.keys(COLOR_THEMES).map(theme => <option key={theme} value={theme}>{theme.replace('-', ' ')}</option>)}
              <option value="custom">Custom colors</option>
            </select></label>
            <label>Text color<input type="color" value={form.text_color} onChange={event => updateColor('text_color', event.target.value)} style={{ ...fieldStyle, height: '42px', padding: '0.25rem' }} /></label>
            <label>Accent color<input type="color" value={form.accent_color} onChange={event => updateColor('accent_color', event.target.value)} style={{ ...fieldStyle, height: '42px', padding: '0.25rem' }} /></label>
            <label>Overlay color<input type="color" value={form.overlay_color} onChange={event => updateColor('overlay_color', event.target.value)} style={{ ...fieldStyle, height: '42px', padding: '0.25rem' }} /></label>
            <label>Slide order<input type="number" min="0" value={form.sort_order} onChange={event => updateForm('sort_order', Number(event.target.value))} style={fieldStyle} /></label>
          </div>
          <div style={{ marginTop: '1rem', padding: '1rem', border: '1px solid var(--light-gray)', borderRadius: '8px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '0.5rem', marginBottom: '0.6rem' }}>
              <strong>Live banner preview</strong><span style={{ color: 'var(--mid-gray)', fontSize: '0.8rem' }}>Drag with sliders below</span>
            </div>
            <div style={{
              position: 'relative', width: '100%', aspectRatio: '16 / 5', minHeight: '180px', overflow: 'hidden',
              borderRadius: '6px', backgroundColor: form.overlay_color, backgroundImage: previewBackground,
              backgroundSize: 'cover', backgroundPosition: `${form.image_position_x}% ${form.image_position_y}%`,
            }}>
              <div style={{ position: 'absolute', left: `${form.text_position_x}%`, top: `${form.text_position_y}%`, transform: 'translate(-50%, -50%)', width: 'min(78%, 720px)', textAlign: form.text_alignment, color: form.text_color, fontFamily: form.font_family }}>
                {form.kicker && <div style={{ color: form.accent_color, fontSize: '0.72rem', letterSpacing: '0.18em', textTransform: 'uppercase', marginBottom: '0.3rem' }}>{form.kicker}</div>}
                <div style={{ fontSize: `${form.font_size}px`, lineHeight: 1.08, fontWeight: 700, overflowWrap: 'anywhere' }}>{form.title || 'Your banner headline'}</div>
                {form.body && <div style={{ marginTop: '0.35rem', fontSize: '0.85rem', overflowWrap: 'anywhere' }}>{form.body}</div>}
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0.7rem 1rem', marginTop: '0.9rem' }}>
              {[
                ['Image horizontal crop', 'image_position_x', 0, 100],
                ['Image vertical crop', 'image_position_y', 0, 100],
                ['Text horizontal position', 'text_position_x', 5, 95],
                ['Text vertical position', 'text_position_y', 10, 90],
                ['Headline size', 'font_size', 20, 84],
              ].map(([label, key, min, max]) => (
                <label key={key} style={{ fontSize: '0.85rem' }}>{label}: {form[key]}{key === 'font_size' ? ' px' : '%'}
                  <input type="range" min={min} max={max} value={form[key]} onChange={event => updateForm(key, Number(event.target.value))} style={rangeStyle} />
                </label>
              ))}
              <label style={{ fontSize: '0.85rem' }}>Text alignment
                <select value={form.text_alignment} onChange={event => updateForm('text_alignment', event.target.value)} style={fieldStyle}>
                  {['left', 'center', 'right'].map(alignment => <option key={alignment} value={alignment}>{alignment}</option>)}
                </select>
              </label>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '0.6rem', marginTop: '1rem' }}>
            <button type="submit" disabled={saving || uploading} style={{ ...fieldStyle, width: 'auto', background: 'var(--charcoal)', color: 'var(--white)', cursor: 'pointer' }}>
              {editingId ? <Pencil size={15} /> : <Plus size={15} />} {saving ? 'Saving...' : editingId ? 'Save banner' : 'Add banner'}
            </button>
            {editingId && <button type="button" onClick={resetForm} style={{ ...fieldStyle, width: 'auto', cursor: 'pointer' }}>Cancel edit</button>}
            {uploading && <span aria-live="polite">Uploading image...</span>}
          </div>
        </form>
      )}

      <div style={{ display: 'grid', gap: '0.65rem' }}>
        {banners.map(banner => (
          <div key={banner.id} style={{ display: 'grid', gridTemplateColumns: '120px 1fr auto', gap: '0.8rem', alignItems: 'center', padding: '0.65rem', borderBottom: '1px solid var(--light-gray)' }}>
            <img src={banner.image_url} alt="" style={{ width: '120px', aspectRatio: '16 / 7', objectFit: 'cover', borderRadius: '5px' }} />
            <div><strong>{banner.title}</strong><div style={{ color: 'var(--mid-gray)', fontSize: '0.82rem' }}>Slide {banner.sort_order + 1} · {banner.is_active ? 'Visible' : 'Hidden'}{banner.archived_at ? ' · Archived' : ''}</div></div>
            <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', justifyContent: 'end' }}>
              {banner.archived_at ? <button type="button" onClick={() => restoreBanner(banner)} style={{ ...fieldStyle, width: 'auto', cursor: 'pointer' }}>Restore</button> : <>
                <button type="button" onClick={() => toggleActive(banner)} style={{ ...fieldStyle, width: 'auto', cursor: 'pointer' }}>{banner.is_active ? 'Hide' : 'Show'}</button>
                <button type="button" onClick={() => startEdit(banner)} style={{ ...fieldStyle, width: 'auto', cursor: 'pointer' }}><Pencil size={14} /> Edit</button>
                <button type="button" onClick={() => archiveBanner(banner)} aria-label={`Archive ${banner.title}`} style={{ ...fieldStyle, width: 'auto', cursor: 'pointer' }}><Trash2 size={14} /></button>
              </>}
            </div>
          </div>
        ))}
        {!banners.length && <p style={{ color: 'var(--mid-gray)', padding: '1rem 0' }}>{showArchived ? 'No archived banners.' : 'No banners yet. Add a banner to enable the slideshow.'}</p>}
      </div>
    </section>
  );
}
